import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { PRI_DOT, PRI_LABEL, STATUS_LABEL, STATUS_BG, STATUS_TEXT } from "../../constants/todoTokens";
import { isTaskOverdue } from "./helpers";
import { visibleAssignees, isSoloView } from "../../../../components/AssigneeGroup/utils";
import { getCurrentUser } from "../../../../utils/currentUser";

const STATUS_META = Object.keys(STATUS_LABEL).reduce((acc, k) => {
  acc[k] = { label: STATUS_LABEL[k], bg: STATUS_BG[k], text: STATUS_TEXT[k] };
  return acc;
}, {});
const DEFAULT_STATUS = STATUS_META[1];

const PRI_COLOR = { 3: "#EF4444", 2: "#F59E0B", 1: "#22C55E" };

function stripHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

const TooltipCard = ({ task, overdue, status, priDot, borderColor }) => {
  // Hover-only, one instance at a time, so an unmemoised read is fine here.
  const me = getCurrentUser();
  const solo = isSoloView(task, { currentUserId: me.userId, isAdmin: me.isAdmin });
  const people = visibleAssignees(task, { currentUserId: me.userId, isAdmin: me.isAdmin });
  const doneCount = people.filter((p) => p.status === 3).length;
  const creator = task.creatorName || task.creatorUserName || null;
  const desc = stripHtml(task.description);
  const dueDate = formatDate(task.actualDeadlineDate);
  const priLabel = PRI_LABEL[task.priority] || "—";
  const priColor = PRI_COLOR[task.priority] || "#94A3B8";

  return (
    <Box sx={{ width: 240, p: "12px 14px", display: "flex", flexDirection: "column", gap: "10px" }}>

      {/* Header row: task number + s.no + priority chip */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {task.taskNo && (
            <Typography sx={{
              fontSize: "10px", fontWeight: 700, color: "#94A3B8",
              fontFamily: "ui-monospace, SFMono-Regular, monospace", lineHeight: 1,
            }}>
              #{task.taskNo}
            </Typography>
          )}
          {task.serialNo && (
            <Box sx={{
              px: "5px", py: "1px", borderRadius: "4px",
              bgcolor: "rgba(3,105,161,0.18)", border: "1px solid rgba(3,105,161,0.25)",
            }}>
              <Typography sx={{
                fontSize: "9px", fontWeight: 700, color: "#7DD3FC",
                fontFamily: "ui-monospace, SFMono-Regular, monospace", lineHeight: 1.4, letterSpacing: "0.04em",
              }}>
                {task.serialNo}
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{
          ml: "auto", display: "flex", alignItems: "center", gap: "4px",
          px: "6px", py: "2px", borderRadius: "20px",
          bgcolor: `${priColor}22`, border: `1px solid ${priColor}44`,
        }}>
          <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: priColor }} />
          <Typography sx={{ fontSize: "9px", fontWeight: 700, color: priColor, lineHeight: 1, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {priLabel}
          </Typography>
        </Box>
      </Box>

      {/* Title */}
      <Typography sx={{
        fontSize: "12px", fontWeight: 700, color: "#F1F5F9", lineHeight: 1.4,
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
      }}>
        {task.title || "Untitled"}
      </Typography>

      {/* Divider */}
      <Box sx={{ height: "1px", bgcolor: "rgba(255,255,255,0.08)" }} />

      {/* Meta rows */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {/* Status */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Typography sx={{ fontSize: "10px", color: "#64748B", width: 56, flexShrink: 0 }}>Status</Typography>
          <Box sx={{
            px: "6px", py: "1px", borderRadius: "4px",
            bgcolor: overdue ? "rgba(239,68,68,0.15)" : `${status.text}22`,
          }}>
            <Typography sx={{ fontSize: "10px", fontWeight: 600, color: overdue ? "#EF4444" : status.text, lineHeight: 1.6 }}>
              {overdue ? "Overdue" : status.label}
            </Typography>
          </Box>
        </Box>

        {/* Assignees. This card is a TOOLTIP, so it deliberately does NOT embed
            AssigneeGroup — a nested popover inside something that closes on
            mouse-out is unusable. Names + status dots only. */}
        {people.length > 0 && (
          <Box sx={{ display: "flex", gap: "6px" }}>
            <Typography sx={{ fontSize: "10px", color: "#64748B", width: 56, flexShrink: 0, mt: "1px" }}>
              {!solo && people.length > 1 ? `Done ${doneCount}/${people.length}` : "Assignee"}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", minWidth: 0 }}>
              {people.slice(0, 4).map((p) => (
                <Box key={p.lineId || p._id} sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: "50%", flexShrink: 0,
                             bgcolor: STATUS_TEXT[p.status] || "#94A3B8" }} />
                  <Typography sx={{ fontSize: "10px", fontWeight: 600, color: "#CBD5E1", lineHeight: 1.4,
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {solo ? "You" : p.name}
                  </Typography>
                </Box>
              ))}
              {people.length > 4 && (
                <Typography sx={{ fontSize: "9px", color: "#64748B" }}>+{people.length - 4} more</Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Support */}
        {task.supportName && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Typography sx={{ fontSize: "10px", color: "#64748B", width: 56, flexShrink: 0 }}>Support</Typography>
            <Typography sx={{ fontSize: "10px", fontWeight: 600, color: "#CBD5E1", lineHeight: 1.4 }}>
              {task.supportName}
            </Typography>
          </Box>
        )}

        {/* Assigned by */}
        {creator && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Typography sx={{ fontSize: "10px", color: "#64748B", width: 56, flexShrink: 0 }}>By</Typography>
            <Typography sx={{ fontSize: "10px", fontWeight: 500, color: "#94A3B8", lineHeight: 1.4 }}>
              {creator}
            </Typography>
          </Box>
        )}

        {/* Acknowledged by */}
        {task.acknowledgedByName && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Typography sx={{ fontSize: "10px", color: "#64748B", width: 56, flexShrink: 0 }}>Ack by</Typography>
            <Typography sx={{ fontSize: "10px", fontWeight: 500, color: "#94A3B8", lineHeight: 1.4 }}>
              {task.acknowledgedByName}
            </Typography>
          </Box>
        )}

        {/* Due date */}
        {dueDate && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Typography sx={{ fontSize: "10px", color: "#64748B", width: 56, flexShrink: 0 }}>Due</Typography>
            <Typography sx={{ fontSize: "10px", fontWeight: 600, color: overdue ? "#EF4444" : "#CBD5E1", lineHeight: 1.4 }}>
              {dueDate}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Description */}
      {desc && (
        <>
          <Box sx={{ height: "1px", bgcolor: "rgba(255,255,255,0.08)" }} />
          <Typography sx={{
            fontSize: "10px", color: "#94A3B8", lineHeight: 1.6,
            display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {desc}
          </Typography>
        </>
      )}
    </Box>
  );
};

const TaskPill = ({ task, onClick, isMobile }) => {
  const overdue = isTaskOverdue(task);
  const priDot = PRI_DOT[task.priority] || "#94A3B8";
  const status = STATUS_META[task.status] || DEFAULT_STATUS;

  // Overdue: keep the status label intact but shift colours to red
  const pillBg = overdue ? "#FFF0F0" : status.bg;
  const labelColor = overdue ? "#DC2626" : status.text;
  const borderColor = overdue ? "#EF4444" : priDot;

  const pill = (
    <Box
      onClick={(e) => { e.stopPropagation(); onClick(task); }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        px: "5px",
        py: "3px",
        borderRadius: "4px",
        bgcolor: pillBg,
        borderLeft: `3px solid ${borderColor}`,
        cursor: "pointer",
        overflow: "hidden",
        transition: "filter 0.12s",
        "&:hover": { filter: "brightness(0.93)" },
      }}
    >
      {/* Priority dot */}
      <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: borderColor, flexShrink: 0 }} />

      {/* taskNo + title — hidden on tiny mobile cells */}
      {!isMobile && (
        <>
          {task.taskNo && (
            <Typography sx={{
              fontSize: "9px", fontWeight: 800, color: borderColor,
              whiteSpace: "nowrap", flexShrink: 0, lineHeight: 1.4,
              fontFamily: "ui-monospace, SFMono-Regular, monospace",
            }}>
              #{task.taskNo}
            </Typography>
          )}
          <Typography sx={{
            fontSize: "10px", fontWeight: 700, color: labelColor,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            flex: 1, lineHeight: 1.4,
          }}>
            {task.title || "Untitled"}
          </Typography>
        </>
      )}

      {/* Status badge */}
      {!isMobile && (
        <Box sx={{ flexShrink: 0, px: "4px", py: "1px", borderRadius: "3px", bgcolor: "rgba(0,0,0,0.06)" }}>
          <Typography sx={{
            fontSize: "9px", fontWeight: 800, color: labelColor,
            whiteSpace: "nowrap", lineHeight: 1.4,
            textTransform: "uppercase", letterSpacing: "0.03em",
          }}>
            {status.label}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Tooltip
      title={<TooltipCard task={task} overdue={overdue} status={status} priDot={priDot} borderColor={borderColor} />}
      placement="right"
      enterDelay={400}
      enterNextDelay={200}
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "#1E293B",
            borderRadius: "10px",
            p: 0,
            boxShadow: "0 16px 40px rgba(0,0,0,0.35), 0 4px 12px rgba(0,0,0,0.2)",
            border: "1px solid rgba(255,255,255,0.08)",
            maxWidth: 268,
            backdropFilter: "blur(12px)",
          },
        },
        arrow: {
          sx: {
            color: "#1E293B",
            "&::before": { border: "1px solid rgba(255,255,255,0.08)" },
          },
        },
      }}
    >
      {pill}
    </Tooltip>
  );
};

export default React.memo(TaskPill);
